// src/components/media/FlatPhotoStrip.tsx — a flat's advert photos, read only, where the advert is edited (the listing
// page). The photos live in the flat's Photos folder in Media now (docs/media-library.md); "Manage photos" opens the
// flat's Images tab.
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Images, Star } from 'lucide-react';
import { Skeleton } from '../ui';
import { MediaThumb } from './MediaThumb';
import { useCan } from '../../hooks/usePermission';
import { mediaApi } from '../../lib/media';

export function FlatPhotoStrip({ unitId, propertyId }: { unitId: string; propertyId: string }) {
  const canRead = useCan('media.read');
  const canCreate = useCan('media.create'); const canUpdate = useCan('media.update');
  const canEdit = canCreate || canUpdate;
  const tree = useQuery({ queryKey: ['media', 'tree', { unitId }], queryFn: () => mediaApi.tree({ unitId }), enabled: canRead });
  const photos = (() => {
    const walk = (list: typeof tree.data): string | undefined => {
      for (const f of list ?? []) { if (f.kind === 'UNIT_PHOTOS' && f.unitId === unitId) return f.id; const hit = walk(f.children); if (hit) return hit; }
      return undefined;
    };
    return walk(tree.data);
  })();
  const items = useQuery({ queryKey: ['media', 'items', photos ?? null], queryFn: () => mediaApi.items(photos!), enabled: !!photos });
  const manage = `/app/properties/${propertyId}/units/${unitId}?tab=images`;

  if (!canRead) return <p className="text-[12.5px] text-ink-500">The advert’s photos are kept in the flat’s Photos folder in Media.</p>;
  const list = items.data ?? [];
  const loading = tree.isLoading || (!!photos && items.isLoading);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-[12px] text-ink-500">From the flat’s Photos folder, in the website’s order. The first one is the cover.</p>
        <Link to={manage} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-ink-200 bg-ink-100 px-3 text-[12px] font-medium text-ink-800 transition hover:bg-ink-200">
          <Images className="h-3.5 w-3.5" />{canEdit ? 'Manage photos' : 'See all photos'}
        </Link>
      </div>
      {loading ? (
        <div className="flex gap-2 overflow-hidden">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="aspect-[4/3] w-36 shrink-0 rounded-lg" />)}</div>
      ) : !photos ? (
        <p className="text-[12.5px] text-ink-400">This flat has no Photos folder yet.</p>
      ) : !list.length ? (
        <p className="text-[12.5px] text-ink-400">No photos yet. {canEdit ? <Link to={manage} className="font-medium text-bronze-600 hover:underline">Add them on the flat’s Images tab.</Link> : null}</p>
      ) : (
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {list.map((it) => (
            <li key={it.id} className="relative w-36 shrink-0 overflow-hidden rounded-lg border border-ink-200 bg-white">
              <div className="aspect-[4/3]"><MediaThumb item={it} /></div>
              {it.isCover && <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[10px] font-semibold text-bronze-700 shadow-sm"><Star className="h-2.5 w-2.5 fill-current" />Cover</span>}
              <p className="truncate px-2 py-1 text-[11.5px] text-ink-600" title={it.caption ?? it.fileName}>{it.displayLabel ?? it.caption ?? it.fileName}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
